import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetAdminActionLogsQuery } from '../../slices/adminApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import Paginate from '../../components/Paginate';


export default function AdminActionLogScreen() {
  const { pageNumber } = useParams();
  const page = Number(pageNumber) || 1;
  const [actionFilter, setActionFilter] = useState('All');

  const { userInfo } = useSelector((state) => state.auth);

  const { data, isLoading, error } = useGetAdminActionLogsQuery(
    userInfo
      ? { page, action: actionFilter }
      : skipToken
  );

  if (!userInfo) return <Loader />;

  const renderError = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.data?.message) return err.data.message;
    if (err.error) return err.error;
    try { return JSON.stringify(err); } catch { return String(err); }
  };

  const actionTypes = [
    'All',
    'DELETE_USER',
    'DELETE_BENCHMARK',
    'DELETE_DRIVE',
    'USER_DELETE_BENCHMARK',
    'BULK_DELETE_BENCHMARKS',
    'DELETE_BLOG_POST',
    'RESTRICT_BLOG_POST',
    'UNRESTRICT_BLOG_POST',
  ];

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return '—';
    return Object.entries(details)
      .map(([key, value]) => `${key}: ${value ?? '—'}`)
      .join(', ');
  };

  return (
    <>
      <Link
        to={`/admin/home`}
        className='btn btn-light'
        style={{ marginTop: '32px', marginBottom: '16px' }}
      >
        Return to Admin Home
      </Link>

      <h1 style={{ marginBottom: '16px' }}>Action Log</h1>

      <div className='d-flex flex-wrap gap-4 mb-4'>
        <div className='d-flex align-items-center'>
          <span className='me-2 fw-bold'>Action Type:</span>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className='form-select form-select--custom w-auto'
          >
            {actionTypes.map((a) => (
              <option className='option--custom' key={a} value={a}>{a === 'All' ? 'All Actions' : a.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{renderError(error)}</Message>
      ) : data.logs.length === 0 ? (
        <Message variant='info'>No action logs found.</Message>
      ) : (
        <div
          className='shadow-sm'
          style={{
            borderRadius: '12px',
            overflow: 'hidden',
            border: '1px solid var(--card-bc)',
            backgroundColor: 'var(--card-bg)',
            padding: '16px',
          }}
        >
          <Table striped hover responsive className='table-sm mb-0'>
            <thead>
              <tr>
                <th>DATE / TIME</th>
                <th>ACTION</th>
                <th>PERFORMED BY</th>
                <th>TARGET TYPE</th>
                <th>TARGET ID</th>
                <th>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {data.logs.map((log) => (
                <tr key={log._id}>
                  <td>
                    {new Date(log.createdAt).toLocaleString([], {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>
                    <span className={`badge bg-${
                      log.action.includes('DELETE') ? 'danger' : 'secondary'
                    }`}>
                      {log.action.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td>{log.performedByEmail}</td>
                  <td>{log.targetType}</td>
                  <td>
                    <code style={{ fontSize: '0.85rem' }}>
                      {log.targetId?.substring(0, 12)}…
                    </code>
                  </td>
                  <td className='small'>{formatDetails(log.details)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div style={{ margin: '32px 0' }}>
        <Paginate
          totalPages={data?.totalPages}
          currentPage={data?.currentPage}
          basePath={`/admin/action_log`}
          firstPageIsBasePath={true}
        />
      </div>
    </>
  );
}
